import React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/styles.css';

const GALLERY_IMAGES = {
  formation: [
    '/assets/images/democracity/democracity_example_1.webp',
    '/assets/images/democracity/democracity_example_2.webp',
  ],
  belvue: [
    '/assets/images/democracity/democracity_example_3.webp',
    '/assets/images/democracity/democracity_example_4.webp',
    '/assets/images/democracity/democracity_example_5.webp',
    '/assets/images/democracity/democracity_example_6.webp',
  ],
  adaptation: [
    '/assets/images/democracity/adaptation_pt_1.webp',
    '/assets/images/democracity/adaptation_pt_2.webp',
    '/assets/images/democracity/adaptation_pt_3.webp',
  ],
  lagoa_musgueira: [
    '/assets/images/democracity/implementation_1.webp',
    '/assets/images/democracity/implementation_2.webp',
  ],
  poiares_lisboa: [
    '/assets/images/democracity/implementation_3.webp',
    '/assets/images/democracity/implementation_4.webp',
  ],
  training: [
    '/assets/images/democracity/training_1.webp',
    '/assets/images/democracity/training_2.webp',
    '/assets/images/democracity/training_3.webp',
    '/assets/images/democracity/training_4.webp',
  ],
};

export const Democracity = () => {
  const { t } = useTranslation(['translation']);
  const [lightboxSlides, setLightboxSlides] = useState<string[]>([]);

  const handleItemClick = (images: string[]) => {
    setLightboxSlides(images);
  };

  const testimonials = t('democracity.testimonials.items', { returnObjects: true }) as {
    name: string;
    affiliation: string;
    quote: string;
  }[];
  const stats = t('democracity.statistics.items', { returnObjects: true }) as {
    number: string;
    text: string;
  }[];
  const observations = t('democracity.introduction.observations', { returnObjects: true }) as string[];

  return (
    <div className="flex flex-col gap-y-16 sm:gap-y-32 democracity max-w-[992px] mx-auto mt-24">
      <div className="z-10 w-full flex flex-col items-center w-full tools-list px-4 sm:px-8">
        <div className="relative px-10 py-5 mx-auto bg-white lg:px-20 lg:py-7 rounded-card">
          <img
            src="/assets/images/other/white_star.svg"
            alt="Star"
            className="absolute -bottom-4 left-2 max-w-[22px] brightness-0 lg:-bottom-8 lg:-left-8 lg:max-w-[40px]"
          />
          <img
            src="/assets/images/other/white_star.svg"
            alt="Star"
            className="absolute -top-4 right-4 max-w-[19px] brightness-0 lg:-top-12 lg:right-1 lg:max-w-[32px]"
          />
          <span className="text-2xl sm:text-2.5xl font-semibold text-center font-title text-secondary-500">
            {t('democracity.title')}
          </span>
        </div>
      </div>

      <section className="px-4 sm:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="my-auto space-y-6">
            <p
              dangerouslySetInnerHTML={{
                __html: t('democracity.introduction.paragraph_1', { interpolation: { escapeValue: false } }),
              }}
            />
            <p
              dangerouslySetInnerHTML={{
                __html: t('democracity.introduction.paragraph_2', { interpolation: { escapeValue: false } }),
              }}
            />
          </div>
          <img
            className="max-w-md mx-auto w-full rounded-xl"
            src="/assets/images/democracity/democracity_game_box.webp"
            alt={t('democracity.introduction.game_box_alt')}
          />
        </div>

        <figure className="my-16">
          <div
            className="grid grid-cols-1 sm:grid-cols-[3fr_2fr] gap-6 cursor-pointer"
            onClick={() => handleItemClick(GALLERY_IMAGES.formation)}
          >
            <img
              className="h-80 w-full rounded-xl object-cover shadow-md"
              src="/assets/images/democracity/democracity_example_1.webp"
              alt={t('democracity.galleries.formation.img_alt_1')}
            />
            <img
              className="h-80 w-full rounded-xl object-cover shadow-md"
              src="/assets/images/democracity/democracity_example_2.webp"
              alt={t('democracity.galleries.formation.img_alt_2')}
            />
          </div>
          <figcaption className="pt-3">
            <h3 className="md:text-lg font-semibold text-gray-900">{t('democracity.galleries.formation.title')}</h3>
            <span className="text-gray-600 text-sm md:text-base">
              {t('democracity.galleries.formation.photos_count')}
            </span>
          </figcaption>
        </figure>

        <div className="my-12 space-y-6">
          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.introduction.paragraph_3', { interpolation: { escapeValue: false } }),
            }}
          />

          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.introduction.paragraph_4', { interpolation: { escapeValue: false } }),
            }}
          />

          <ul className="list-disc pl-5 space-y-2">
            {observations.map((obs, i) => (
              <li key={i}>{obs}</li>
            ))}
          </ul>

          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.introduction.target_audience', { interpolation: { escapeValue: false } }),
            }}
          />
        </div>

        <figure className="my-16">
          <div
            className="grid grid-cols-1 gap-6 lg:grid-cols-[1.5fr_1fr_1.5fr] cursor-pointer"
            onClick={() => handleItemClick(GALLERY_IMAGES.belvue)}
          >
            <img
              className="h-64 w-full rounded-xl object-cover shadow-md lg:h-full"
              src="/assets/images/democracity/democracity_example_3.webp"
              alt={t('democracity.galleries.belvue.img_alt_1')}
            />
            <div className="flex flex-col gap-4">
              <img
                className="h-32 w-full rounded-xl object-cover shadow-md lg:h-full"
                src="/assets/images/democracity/democracity_example_4.webp"
                alt={t('democracity.galleries.belvue.img_alt_2')}
              />
              <img
                className="h-32 w-full rounded-xl object-cover shadow-md lg:h-full"
                src="/assets/images/democracity/democracity_example_5.webp"
                alt={t('democracity.galleries.belvue.img_alt_3')}
              />
            </div>
            <img
              className="h-64 w-full rounded-xl object-cover shadow-md lg:h-full"
              src="/assets/images/democracity/democracity_example_6.webp"
              alt={t('democracity.galleries.belvue.img_alt_4')}
            />
          </div>
          <figcaption className="pt-3">
            <h3 className="md:text-lg font-semibold text-gray-900">{t('democracity.galleries.belvue.title')}</h3>
            <span className="text-gray-600 text-sm md:text-base">{t('democracity.galleries.belvue.photos_count')}</span>
          </figcaption>
        </figure>
      </section>

      <section className="px-4 sm:px-8">
        <h2 className="text-2xl sm:text-2.5xl font-semibold font-title text-secondary-500 w-full text-center">
          {t('democracity.origin_adaptation.title')}
        </h2>
        <div className="my-12 space-y-6">
          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.origin_adaptation.paragraph_1', { interpolation: { escapeValue: false } }),
            }}
          />
          <p>{t('democracity.origin_adaptation.paragraph_2')}</p>
          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.origin_adaptation.paragraph_3', { interpolation: { escapeValue: false } }),
            }}
          />
          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.origin_adaptation.paragraph_4', { interpolation: { escapeValue: false } }),
            }}
          />
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-[3fr_4fr_3fr] gap-6 my-16 cursor-pointer"
          onClick={() => handleItemClick(GALLERY_IMAGES.adaptation)}
        >
          <img
            className="h-80 w-full rounded-xl object-cover shadow-md"
            src="/assets/images/democracity/adaptation_pt_1.webp"
            alt={t('democracity.galleries.adaptation.img_alt_1')}
          />
          <img
            className="h-80 w-full rounded-xl object-cover shadow-md"
            src="/assets/images/democracity/adaptation_pt_2.webp"
            alt={t('democracity.galleries.adaptation.img_alt_2')}
          />
          <img
            className="h-80 w-full rounded-xl object-cover shadow-md"
            src="/assets/images/democracity/adaptation_pt_3.webp"
            alt={t('democracity.galleries.adaptation.img_alt_3')}
          />
        </div>
      </section>

      <section className="px-4 sm:px-8">
        <h2 className="text-2xl sm:text-2.5xl font-semibold font-title text-secondary-500 text-center">
          {t('democracity.statistics.title')}
        </h2>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-x-16 my-16">
          <img
            className="w-full rounded-xl mx-auto"
            src="/assets/images/democracity/implementation_map.webp"
            alt={t('democracity.statistics.map_alt')}
          />
          <div className="grid grid-cols-[auto_1fr] gap-x-6 py-8 gap-y-4 my-auto items-center">
            {stats.map((item, i) => (
              <React.Fragment key={i}>
                <span className="font-title text-3xl h-auto font-bold text-right text-primary-500">{item.number}</span>
                <span className="text-gray-700 text-sm">{item.text}</span>
              </React.Fragment>
            ))}
          </div>{' '}
        </div>
      </section>

      <section className="px-4 sm:px-8">
        <h2 className="text-2xl sm:text-2.5xl font-semibold font-title text-secondary-500 text-center">
          {t('democracity.pilot_implementations.title')}
        </h2>
        <div className="my-12 space-y-6">
          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.pilot_implementations.paragraph_1', { interpolation: { escapeValue: false } }),
            }}
          />
          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.pilot_implementations.paragraph_2', { interpolation: { escapeValue: false } }),
            }}
          />
          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.pilot_implementations.paragraph_3', { interpolation: { escapeValue: false } }),
            }}
          />
        </div>

        <figure className="my-16">
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-6 cursor-pointer"
            onClick={() => handleItemClick(GALLERY_IMAGES.lagoa_musgueira)}
          >
            <img
              className="h-80 w-full rounded-xl object-cover shadow-md"
              src="/assets/images/democracity/implementation_1.webp"
              alt={t('democracity.galleries.lagoa_musgueira.img_alt_1')}
            />
            <img
              className="h-80 w-full rounded-xl object-cover shadow-md"
              src="/assets/images/democracity/implementation_2.webp"
              alt={t('democracity.galleries.lagoa_musgueira.img_alt_2')}
            />
          </div>
          <figcaption className="pt-3">
            <h3 className="md:text-lg font-semibold text-gray-900">
              {t('democracity.galleries.lagoa_musgueira.title')}
            </h3>
            <span className="text-gray-600 text-sm md:text-base">
              {t('democracity.galleries.lagoa_musgueira.photos_count')}
            </span>
          </figcaption>
        </figure>

        <div className="my-12 space-y-6">
          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.pilot_implementations.paragraph_4', { interpolation: { escapeValue: false } }),
            }}
          />
          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.pilot_implementations.paragraph_5', { interpolation: { escapeValue: false } }),
            }}
          />
          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.pilot_implementations.paragraph_6', { interpolation: { escapeValue: false } }),
            }}
          />
        </div>

        <figure className="my-16">
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-6 cursor-pointer"
            onClick={() => handleItemClick(GALLERY_IMAGES.poiares_lisboa)}
          >
            <img
              className="h-80 w-full rounded-xl object-cover shadow-md"
              src="/assets/images/democracity/implementation_3.webp"
              alt={t('democracity.galleries.poiares_lisboa.img_alt_1')}
            />
            <img
              className="h-80 w-full rounded-xl object-cover shadow-md"
              src="/assets/images/democracity/implementation_4.webp"
              alt={t('democracity.galleries.poiares_lisboa.img_alt_2')}
            />
          </div>
          <figcaption className="pt-3">
            <h3 className="md:text-lg font-semibold text-gray-900">
              {t('democracity.galleries.poiares_lisboa.title')}
            </h3>
            <span className="text-gray-600 text-sm md:text-base">
              {t('democracity.galleries.poiares_lisboa.photos_count')}
            </span>
          </figcaption>
        </figure>
      </section>

      <section className="px-4 sm:px-8">
        <h2 className="text-2xl sm:text-2.5xl font-semibold font-title text-secondary-500 text-center">
          {t('democracity.testimonials.title')}
        </h2>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 my-16 items-end">
          {testimonials.map((testimonial, i) => (
            <blockquote key={i} className="bg-white rounded-2xl shadow-md p-8">
              <header className="mb-4">
                <h3 className="font-title text-xl text-gray-900">{testimonial.name}</h3>
                <p className="text-sm text-gray-500 italic">{testimonial.affiliation}</p>
              </header>
              <p
                dangerouslySetInnerHTML={{
                  __html: testimonial.quote,
                }}
              />
            </blockquote>
          ))}
        </div>
      </section>

      <section className="px-4 sm:px-8 w-full">
        <h2 className="text-2xl sm:text-2.5xl font-semibold font-title text-secondary-500 text-center">
          {t('democracity.training.title')}
        </h2>
        <div className="my-12 space-y-6">
          <p>{t('democracity.training.paragraph_1')}</p>
          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.training.paragraph_2', { interpolation: { escapeValue: false } }),
            }}
          />
          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.training.paragraph_3', { interpolation: { escapeValue: false } }),
            }}
          />
          <p
            dangerouslySetInnerHTML={{
              __html: t('democracity.training.paragraph_4', { interpolation: { escapeValue: false } }),
            }}
          />
          <p>{t('democracity.training.paragraph_5')}</p>
        </div>

        <figure className="my-16">
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-6 cursor-pointer"
            onClick={() => handleItemClick(GALLERY_IMAGES.training)}
          >
            <img
              className="h-80 w-full rounded-xl object-cover shadow-md"
              src="/assets/images/democracity/training_1.webp"
              alt={t('democracity.galleries.training.img_alt_1')}
            />
            <img
              className="h-80 w-full rounded-xl object-cover shadow-md"
              src="/assets/images/democracity/training_2.webp"
              alt={t('democracity.galleries.training.img_alt_2')}
            />
            <img
              className="h-80 w-full rounded-xl object-cover shadow-md"
              src="/assets/images/democracity/training_3.webp"
              alt={t('democracity.galleries.training.img_alt_3')}
            />
            <img
              className="h-80 w-full rounded-xl object-cover shadow-md"
              src="/assets/images/democracity/training_4.webp"
              alt={t('democracity.galleries.training.img_alt_4')}
            />
          </div>
          <figcaption className="pt-3">
            <h3 className="md:text-lg font-semibold text-gray-900">{t('democracity.galleries.training.title')}</h3>
            <span className="text-gray-600 text-sm md:text-base">
              {t('democracity.galleries.training.photos_count')}
            </span>
          </figcaption>
        </figure>

        <div className="flex flex-wrap justify-center gap-12 items-center mt-24 mb-16">
          <img
            className="h-20 w-auto"
            src="/assets/images/democracity/king_bandouin_foundation.webp"
            alt={t('democracity.partners_logo_alt_1')}
          />
          <img
            className="h-20 w-auto"
            src="/assets/images/democracity/fundacao_calouste_gulbenkian.webp"
            alt={t('democracity.partners_logo_alt_2')}
          />
          <img
            className="h-20 w-auto"
            src="/assets/images/democracity/mypolis_logo.webp"
            alt={t('democracity.partners_logo_alt_3')}
          />
        </div>
      </section>

      <Lightbox
        open={lightboxSlides.length > 0}
        close={() => setLightboxSlides([])}
        slides={lightboxSlides.map((img) => ({ src: img }))}
        plugins={[Thumbnails]}
      />
    </div>
  );
};

export default Democracity;
